const propHasAnswer = '__hasAnswer'
const propShouldRetainId = '__shouldRetainId'

/**
 * Remove the nodes that don't have answers in themselves or their children
 */
export function removeNodesWithoutAnswers(actionCardsData: any) {
  // Mark all nodes that have an answer, and their children
  traverseNodes(actionCardsData, '*', (obj: any, key: string, _: any, parentNodes: any[]) => {
    const isFormQuestion =
      obj['kind'] === 'Question' || // Question within 'Content' section (#11428)
      parentNodes[1]?.['questions']?.includes(obj) // Legacy 'Form' section type support
    const isAnswerProperty =
      (key === 'value' && isFormQuestion) || // It's a form question (#10549)
      key === 'freeTextValue' ||
      (key === 'checked' && !obj.responses) // Ignore "checked" prop of assessments themselves
    const containsAnswer = !!obj[key] || typeof obj[key] === 'number'
    if (isAnswerProperty && containsAnswer) {
      obj[propHasAnswer] = true
      // Go through all child nodes and mark them as having an answer
      traverseNodes(obj, '*', (child: any) => {
        child[propHasAnswer] = true
      })
      // If this node is a dropdown - remove all options in 'data' that are not selected.
      // Also, the selected option should retain its ID to match the 'value' property (#8503)
      if (obj.type === 'Dropdown') {
        obj.data = obj.data
          .filter((option: any) => option.id === obj.value)
          .map((option: any) => ({ [propShouldRetainId]: true, ...option }))
      }
    }
  })

  // Mark all nodes that don't have an answer, but have children with answers
  traverseNodes(actionCardsData, '*', (obj: any) => {
    if (obj[propHasAnswer] === undefined) {
      obj[propHasAnswer] = false
      // If any child node contains an answer, mark this node as also having an answer
      traverseNodes(obj, '*', (child: any) => {
        if (child !== obj && child[propHasAnswer]) {
          obj[propHasAnswer] = true
        }
      })
    }
  })

  // Remove all nodes that don't have an answer
  // in themselves or their children
  traverseNodes(actionCardsData, '*', (obj: any, key: string) => {
    if (obj[key] && typeof obj[key] === 'object' && !obj[key][propHasAnswer]) {
      delete obj[key]
    }
  })

  // Remove all null elements from arrays
  if (Array.isArray(actionCardsData)) {
    actionCardsData = actionCardsData.filter((item: any) => item !== null)
  }
  traverseNodes(actionCardsData, '*', (obj: any, key: string) => {
    if (Array.isArray(obj[key])) {
      obj[key] = obj[key].filter((item: any) => item !== null)
    }
  })

  return actionCardsData
}

/**
 * Remove all nodes that are marked as "excluded from AI"
 * @story #6703
 */
export function removeExcludedNodes(actionCardsData: any) {
  // Remove all nodes that don't have an answer
  // in themselves or their children
  traverseNodes(actionCardsData, '*', (obj: any, key: string) => {
    if (obj[key] && typeof obj[key] === 'object' && obj[key]['isExcludedFromAi']) {
      delete obj[key]
    }
  })
  return actionCardsData
}

/**
 * Remove all ID properties from the Action Card JSON
 */
export function removeIds(actionCardsData: any) {
  traverseNodes(actionCardsData, '*', (obj, key) => {
    if (obj[propShouldRetainId]) return
    if (key === 'id') {
      delete obj[key]
    }
  })
  return actionCardsData
}

/**
 * Remove all properties from the Action Card JSON which are non-essential for AI processing
 */
export function removeNonessentialProperties(actionCardsData: any) {
  const propertiesToIgnore = new Set([
    'isAutoCollapse',
    'isOpened',
    'score',
    'tags',
    'rank',
    'color',
    'colorCode',
    'parent',
    'templateId',
    'isRecommendation',
    'isExcludedFromAi',
    '_originalId',
    'code',
    'hint',
    'idOfSep',
    'hasFreeText',
    'altScoring',
    'altScoringMax',
    propHasAnswer,
    propShouldRetainId,
  ])

  traverseNodes(actionCardsData, '*', (obj: any, key: string) => {
    if (propertiesToIgnore.has(key)) {
      delete obj[key]
    }
  })

  return actionCardsData
}

/**
 * Remove all values from the Action Card
 */
export function removeValues(actionCardsData: any) {
  traverseNodes(actionCardsData, '*', (obj, key) => {
    if (key === 'checked' || key === 'value') {
      delete obj[key]
    }
  })
  return actionCardsData
}

/**
 * Remove all Action Card section names and IDs (#4377)
 */
export function removeSectionNames(actionCardsData: any) {
  traverseNodes(actionCardsData, '*', (obj, _, parentNode) => {
    if (!parentNode) {
      delete obj.name
      delete obj.id
    }
  })
  return actionCardsData
}

/**
 * Get Acton Card node by ID
 * @story #7852
 */
export function getNode(actionCardsData: any, id: number | string): any {
  let foundNode = null
  traverseNodes(actionCardsData, '*', (obj, key) => {
    if (obj[key]?.id == id) {
      foundNode = obj[key]
    }
  })
  return foundNode
}

/**
 * Recursively traverse all nodes in the Action Card object
 */
export function traverseNodes(
  obj: any,
  keyToFind: string,
  callback: (obj: any, key: string, parentNode: any, parentNodes: any[]) => void,
  parentNode?: any,
  parentNodes: any[] = [],
) {
  if (typeof obj !== 'object' || obj === null) {
    return
  }

  for (const key in obj) {
    if (key === keyToFind || keyToFind === '*') {
      callback(obj, key, parentNode, parentNodes)
    }
    if (typeof obj[key] === 'object') {
      traverseNodes(obj[key], keyToFind, callback, obj, [obj, ...parentNodes])
    }
  }
}
